import { SearchResponse } from '@algolia/client-search'
import { getInitialSelectedVariant } from '@src/components/product-card/product-card.helpers'
import { IProductCard } from '@src/components/product-card/product-card.types'
import BATCHED_EVENT_LIMIT from '@src/constants/batchedEventLimit'
import { LoaderWrapper } from '@src/quince-components/src'
import {
  getIndividualTagStatus,
  ProductTagKey,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useFetch from '@src/quince-site-common/src/hooks/useFetch'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import SomethingWentWrong from '@src/quince-site-common/src/organisms/somethingWentWrong'
import siteStateManager, { SearchDetails } from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { AlgoliaSearchResult } from '@src/quince-site-common/src/services/algoliaService/algolia.types'
import productService from '@src/quince-site-common/src/services/productService'
import { batchedAnalytics } from '@src/quince-site-common/src/utils/batched-analytics'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { getProductDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductDataForBatchedAnalytics'
import { getProductTagsDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductTagsDataForBatchedAnalytics'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { transformTagsIntoAnalyticsTags } from '@src/utils/general.utils'
import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { SetStateAction, useEffect, useMemo, useState } from 'react'

import { getTrackingMetaData, PRODUCTS_LIMIT_FOR_EVENT } from './searchResults.constants'
import { isItemInWishList } from './searchResults.helpers'
import styles from './searchResults.module.scss'
import { SearchResultsContent } from './SearchResultsContent'

export interface SearchResultsProps {
  searchResults?: SearchResponse<AlgoliaSearchResult>
  searchQuery: string
  tracker?: string
  isFetchingSearchResults: boolean
  onLoadMore: () => void
  setIsFetchingSearchResults: (value: SetStateAction<boolean>) => void
  isFetchingMoreSearchResults: boolean
  isFirstPage: boolean
  hasMorePageToLoad: boolean
}

const SearchResults = (props: SearchResultsProps) => {
  const {
    onLoadMore,
    isFetchingSearchResults,
    isFetchingMoreSearchResults,
    isFirstPage,
    hasMorePageToLoad,
    searchResults,
    setIsFetchingSearchResults,
  } = props

  const shouldTriggerSearchCompleteEvent = useAppSelector<SearchDetails>(
    siteStateManager,
    siteStateManager.getSearchDetails,
  ).shouldTriggerSearchCompleteEvent

  const url = isBrowser() && new URL(window.location.href)
  const hideSearchHeader = url && url.searchParams.get('hide_header') == 'true'

  const [showLoader, setShowLoader] = useState(true)
  const deviceInfo = useDeviceInfo()
  const { zipCode } = useZipCode()
  const maxWidth = getMaxWidthInPx({
    maxWidthMobileInRem: styles.productCardWidthMobile ?? '',
    maxWidthDesktopInRem: styles.productCardWidth ?? '',
    deviceType: deviceInfo.deviceType,
  })
  const {
    fetchData,
    isLoading: isFetchingProductCards,
    response: productCards,
    hasError,
    error,
  } = useFetch<IProductCard[], [AlgoliaSearchResult[] | undefined, IProductCard[] | undefined, string | undefined]>(
    fetchProductCardsForSearchResult,
    {
      initialData: [],
      initialLoadingState: false,
      onlyReturnLatestFetch: true,
    },
  )

  useEffect(() => {
    if (hasError) {
      captureException(error, {
        feature: FEATURES.GET_PRODUCT,
        module: MODULES.SEARCH,
        extraInfo: {
          // @i18n-ignore
          capturedFrom: 'fetchProductCardsForSearchResult in SearchResults',
        },
      })
    }
  }, [error, hasError])

  useEffect(() => {
    setShowLoader(!searchResults || (isFetchingProductCards && isFirstPage) || isFetchingSearchResults)
  }, [searchResults, isFetchingProductCards, isFetchingSearchResults])

  // Get Search Result should only be called after algolia search is completed.
  const isAlgoliaSearchCompleted = searchResults?.hits !== undefined

  useEffect(() => {
    if (!isAlgoliaSearchCompleted) return

    void fetchData(searchResults.hits, !isFirstPage ? productCards : [], zipCode)
      .then(searchCards => {
        if (shouldTriggerSearchCompleteEvent) {
          let eventData = getSearchCompletedEventData(searchCards?.slice(0, PRODUCTS_LIMIT_FOR_EVENT) ?? [])
          if (searchCards?.length) {
            const updatedProducts = searchCards.slice(0, PRODUCTS_LIMIT_FOR_EVENT).map(({ product }, index) => ({
              ...eventData.products[index],
              wishListed: isItemInWishList(product),
            }))
            eventData = {
              ...eventData,
              products: updatedProducts,
            }
          }
          eventData = {
            ...eventData,
            products: eventData.products,
            searchQuery: props.searchQuery,
            queryLength: props.searchQuery.length,
            numberOfSearchResults: props.searchResults?.length ?? 0,
          }
          trackEvent(TELEMETRY_EVENT_NAMES.SEARCH_COMPLETED, eventData)
          siteStateManager.setShouldTriggerSearchCompleteEvent(false)
        }
      })
      .finally(() => {
        setIsFetchingSearchResults(false)
      })
  }, [fetchData, props.searchResults, props.searchResults?.hits, zipCode])

  const intersectionOffset = isBrowser() ? window.innerHeight / 4 : 200
  const [intersectRef, isIntersecting] = useIntersect(null, `${intersectionOffset}px`, 0, false, !hasMorePageToLoad)

  useEffect(() => {
    if (isIntersecting && !isFetchingSearchResults && !isFetchingProductCards && !isFetchingMoreSearchResults) {
      onLoadMore()
    }
  }, [isIntersecting])

  const showLoadMoreLoader = isFetchingMoreSearchResults || (!isFirstPage && isFetchingProductCards)

  const productCardLength = hasMorePageToLoad ? searchResults?.nbHits : productCards?.length

  const productViewTracker = useMemo(() => props.tracker && `${props.tracker}__search_results`, [props.tracker])

  useEffect(() => {
    if (productViewTracker) {
      batchedAnalytics.initializeBatchedAnalyticsEvent({
        tracker: productViewTracker,
        eventLimit: BATCHED_EVENT_LIMIT[TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW],
        debouncePeriod: 1000,
      })
      batchedAnalytics.trackBatchedAnalytics({
        id: `internalAnalytics-${productViewTracker}`,
        tracker: productViewTracker,
        callback: ({ events }) => {
          const productList = events.map(event => getProductDataForBatchedAnalytics(event))
          const tagsCount = getProductTagsDataForBatchedAnalytics(productList)
          trackEvent(TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW, {
            productList: productList,
            tagsCount: tagsCount,
            ...getTrackingMetaData().context,
          })
        },
      })
    }
  }, [])
  const { t } = useTranslation('common')
  if (hasError) {
    return (
      <SomethingWentWrong
        wrapperClass={styles.somethingWentWrongWrapper}
        // @i18n-ignore
        minHeight={'20rem'}
      />
    )
  }

  return (
    <div
      className={classnames(styles.searchResultsContainer, {
        [styles.searchResultsContainer__paddingTop ?? '']: hideSearchHeader,
      })}
    >
      {props.searchQuery && (
        <div className={styles.searchResultsContainer__loaderWrapper}>
          {!hideSearchHeader && (
            <div className={styles.searchResultsHeader}>
              {/* @i18n-ignore */}
              <h1 className={styles.searchResultsHeader__title}>{`"${props.searchQuery.toUpperCase()}"`}</h1>
              {productCards && productCards.length > 0 && (
                <p className={styles.searchResultsHeader__subtitle}>
                  {t('showing')} {productCardLength} {t('product_results')}
                </p>
              )}
            </div>
          )}
          <SearchResultsContent
            productCards={productCards ?? []}
            showLoader={showLoader}
            maxWidth={maxWidth}
            productViewTracker={productViewTracker}
            searchQuery={props.searchQuery}
          />

          <div id="boundary_bottom" ref={intersectRef} />
          <LoaderWrapper
            showLoader={showLoadMoreLoader}
            // @i18n-ignore
            minHeight={'20rem'}
          >
            <div />
          </LoaderWrapper>
        </div>
      )}
    </div>
  )
}

const fetchProductCardsForSearchResult = async (
  searchResults: AlgoliaSearchResult[] | undefined,
  alreadyPresentResults: IProductCard[],
  zipCode?: string,
): Promise<IProductCard[]> => {
  if (!searchResults || searchResults.length === 0) {
    return []
  }
  const productVsColor = searchResults.map(item => item.product_id.toString() + '_' + item.color).join(',')
  const { data: searchProductResult } = await productService.getSearchResults({
    productColors: productVsColor,
    zipCode: zipCode,
  })

  return [...alreadyPresentResults, ...searchProductResult]
}

const getSearchCompletedEventData = (data: IProductCard[]) => {
  if (data.length) {
    let oos_product_count = 0
    const products = data.map(({ product }: IProductCard, index: number) => {
      const { classification, relatedIds, title, colorVariants, reviews } = product
      const initialSelectedVariant = getInitialSelectedVariant({ cardVariants: product.cardVariants })
      let productData = {
        product_id: relatedIds.shopifyId,
        name: title,
        position: index + 1,
        color: initialSelectedVariant?.displayConfig.value,
        count_color_variant: colorVariants?.length,
        rating: reviews.averageRating,
        tags: initialSelectedVariant?.tags.map(item => item.value) ?? [],
        tag_ids: transformTagsIntoAnalyticsTags(initialSelectedVariant?.tags.map(item => item.value) ?? []),
        department: classification?.department,
        division: classification?.division,
        subdepartment: classification?.subdepartment,
      }
      if (productData.tags.includes('Sold Out')) {
        oos_product_count++
      }
      productData = { ...productData, ...getIndividualTagStatus(productData.tags as ProductTagKey[]) }

      return productData
    })

    return { products, result_count: products.length, null_search: false, oos_product_count }
  } else {
    return {
      null_search: true,
      result_count: 0,
      products: [],
    }
  }
}

export default SearchResults
